import { Component, forwardRef, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { callingCountries } from 'country-data-list';
import {
  appendExt,
  buildPhoneValue,
  formatNationalForCountry,
  isFictionalNanpNational,
  parsePhone,
  stripExt,
} from 'app/_utils/phone-format';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }]
})

export class PhoneInputComponent implements ControlValueAccessor, Validator {
  @Input() required: boolean = false;
  private _model: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  countryDatas: any[] = [];
  phoneNumber: string;
  phoneCountryCode: string = '+1';
  hostControl: AbstractControl | null = null;

  constructor() {
    const allCountries = callingCountries.all
      .map(item => ({ name: item.name, phone_code: item.countryCallingCodes[0] }))
      .filter(item => item.phone_code); // Filter out items without phone codes
    
    // Separate United States from others
    const unitedStates = allCountries.find(c => c.phone_code === '+1' && c.name === 'United States');
    const otherCountries = allCountries.filter(c => !(c.phone_code === '+1' && c.name === 'United States'));
    
    // Sort other countries alphabetically
    otherCountries.sort((a, b) => a.name.localeCompare(b.name));
    
    // Put United States first, then all other countries
    this.countryDatas = unitedStates ? [unitedStates, ...otherCountries] : otherCountries;
  }

  writeValue(value: any) {
    this._model = value;
    if (this._model) {
      this.dividePhoneNumber();
    } else {
      this.phoneNumber = '';
      this.phoneCountryCode = '+1';
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  updatePhoneNumber() {
    const { rest, ext } = stripExt(this.phoneNumber || '');
    const base = formatNationalForCountry(this.phoneCountryCode, rest);
    this.phoneNumber = base ? appendExt(base, ext) : '';
    const stored = buildPhoneValue(this.phoneCountryCode, rest);
    this._model = stored ? appendExt(stored, ext) : stored;
    if (this.onChange) {
      this.onChange(this._model || null);
    }
    if (this.onTouched) {
      this.onTouched(this._model);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.hostControl = control;
    const raw = control.value == null ? '' : String(control.value).trim();
    if (!raw) {
      return null;
    }
    // Stored US/NANP values carry no +CC prefix, so parse with a US default region.
    const parsed = parsePhoneNumberFromString(raw, 'US');
    if (!parsed) {
      return { invalidPhone: true };
    }
    if (parsed.isValid()) {
      return null;
    }
    // Demo / fiction NANP 555 numbers (e.g. Wendy Brant) are isPossible but not isValid.
    if (
      parsed.isPossible()
      && parsed.countryCallingCode === '1'
      && isFictionalNanpNational(parsed.nationalNumber)
    ) {
      return null;
    }
    return { invalidPhone: true };
  }

  get hasInvalidValue(): boolean {
    const control = this.hostControl;
    return !!(control && control.errors && control.errors.invalidPhone);
  }

  get showInvalid(): boolean {
    const control = this.hostControl;
    return this.hasInvalidValue && !!(control && (control.dirty || control.touched));
  }

  dividePhoneNumber() {
    if (!this._model) {
      this.phoneNumber = '';
      this.phoneCountryCode = '+1';
      return;
    }

    const raw = String(this._model);
    const { rest, ext } = stripExt(raw);
    const parsed = parsePhone(raw);
    if (parsed && parsed.countryCode) {
      this.phoneCountryCode = `+${parsed.countryCode}`;
      this.phoneNumber = appendExt(formatNationalForCountry(this.phoneCountryCode, parsed.nationalDigits), ext);
      return;
    }

    // Fallback: legacy " +CC rest" split on last space when unparseable
    const i = rest.lastIndexOf(' ');
    if (i >= 0 && rest.startsWith('+')) {
      this.phoneCountryCode = rest.substring(0, i);
      this.phoneNumber = appendExt(formatNationalForCountry(this.phoneCountryCode, rest.substring(i + 1)), ext);
    } else {
      this.phoneCountryCode = '+1';
      this.phoneNumber = appendExt(formatNationalForCountry('+1', rest), ext);
    }
  }
}
